/*
 * Adapted from Bencho "Stacked bars" (https://bencho.dev/blocks/stacked-bars).
 *
 * MIT License
 *
 * Copyright (c) 2026 Lorenzo Cabra
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { animate, type MotionValue, motion, useMotionValue, useReducedMotion } from 'framer-motion'
import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { cn } from '~/shared/lib/utils'
import {
  barGeometry,
  pointLabels,
  type StackedBarsBucket,
  type StackedBarsPoint,
  sumValues,
} from './stacked-bars-data'
import './stacked-bars.css'

const SEG_GAP = 2
const LIFT = 2
const PREFERRED_GAP = 6
const TIP_W = 172
const TIP_OFFSET = 8
const MAX_STAGGER_MS = 420
const SPRING = { type: 'spring' as const, stiffness: 380, damping: 34 }
const SKELETON_HEIGHTS = [38, 54, 46, 70, 62, 48, 80, 66, 58, 74, 52, 86, 68, 60, 78, 92]

/** One stacked layer; the first segment sits on the baseline. */
export interface StackedBarsSegment<K extends string> {
  key: K
  label: string
  /** Any CSS color, typically a theme variable such as `var(--chart-1)`. */
  color: string
}

interface StackedBarsPeriod<P extends string> {
  value: P
  label: string
}

interface StackedBarsHeadline {
  value: number
  label: string
  /** Relative change as a fraction (0.12 = +12 %); null or omitted hides it. */
  change?: number | null
  changeLabel?: string
}

interface StackedBarsProps<K extends string, P extends string> {
  series: readonly StackedBarsPoint<K>[]
  segments: readonly StackedBarsSegment<K>[]
  bucket?: StackedBarsBucket
  title?: string
  icon?: ReactNode
  subtitle?: string
  headline?: StackedBarsHeadline
  periods?: readonly StackedBarsPeriod<P>[]
  period?: P
  onPeriodChange?: (period: P) => void
  loading?: boolean
  emptyText?: string
  /** Plot height in px. */
  height?: number
  /** Show segment chips that hide and show a segment. */
  legend?: boolean
  locale?: string
  className?: string
}

interface ColumnLayout {
  segments: { key: string; color: string; bottom: number; height: number }[]
  top: number
}

function useElementWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    setWidth(el.getBoundingClientRect().width)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])
  return width
}

function useSeenOnce(ref: React.RefObject<HTMLElement | null>): boolean {
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || seen) return
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true)
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setSeen(true)
          observer.disconnect()
        }
      },
      { threshold: 0.3 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, seen])
  return seen
}

function useGrown(dataKey: string, seen: boolean, reduced: boolean): boolean {
  const [grownKey, setGrownKey] = useState<string | null>(null)
  useEffect(() => {
    if (!seen) return
    if (reduced) {
      setGrownKey(dataKey)
      return
    }
    let second = 0
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setGrownKey(dataKey))
    })
    return () => {
      cancelAnimationFrame(first)
      cancelAnimationFrame(second)
    }
  }, [dataKey, seen, reduced])
  return grownKey === dataKey
}

function glide(value: MotionValue<number>, to: number, jump: boolean) {
  if (jump) value.set(to)
  else animate(value, to, SPRING)
}

export function StackedBars<K extends string, P extends string = string>({
  series,
  segments,
  bucket = 'day',
  title,
  icon,
  subtitle,
  headline,
  periods,
  period,
  onPeriodChange,
  loading = false,
  emptyText,
  height = 160,
  legend = false,
  locale: localeProp,
  className,
}: StackedBarsProps<K, P>) {
  const locale = localeProp ?? getLocale()
  const reduced = useReducedMotion() ?? false
  const root = useRef<HTMLDivElement | null>(null)
  const plot = useRef<HTMLDivElement | null>(null)
  const summaryId = useId()
  const hintId = useId()
  const width = useElementWidth(root)
  const seen = useSeenOnce(root)
  const [at, setAt] = useState<number | null>(null)
  const [hidden, setHidden] = useState<ReadonlySet<K>>(() => new Set())

  const number = useMemo(() => new Intl.NumberFormat(locale), [locale])
  const fmt = (value: number) => number.format(Math.round(value))

  const visible = segments.filter((segment) => !hidden.has(segment.key))
  const visibleKeys = visible.map((segment) => segment.key)
  const allKeys = segments.map((segment) => segment.key)
  const n = series.length
  const empty = n === 0 || series.every((point) => sumValues(point.values, allKeys) === 0)

  const dataKey = `${n}|${series[0]?.date ?? ''}|${series.at(-1)?.date ?? ''}`
  const grown = useGrown(dataKey, seen, reduced)

  const labels = series.map((point) => pointLabels(point, bucket, locale))
  const totals = series.map((point) => sumValues(point.values, visibleKeys))
  const maxTotal = Math.max(1, ...totals)
  const chartHeight = height - 1
  const { gap, barWidth } = barGeometry(width, n, PREFERRED_GAP)
  const radius = Math.min(2.5, barWidth / 2)
  const usable = chartHeight - LIFT - 2 - SEG_GAP * Math.max(0, visible.length - 1)
  const scale = usable / maxTotal
  const stepMs = n > 0 ? Math.min(14, MAX_STAGGER_MS / n) : 14

  const layout: ColumnLayout[] = series.map((point) => {
    let y = 0
    const stacked: ColumnLayout['segments'] = []
    for (const segment of visible) {
      const value = point.values[segment.key] ?? 0
      if (value <= 0) continue
      const segmentHeight = Math.max(2, value * scale)
      stacked.push({ key: segment.key, color: segment.color, bottom: y, height: segmentHeight })
      y += segmentHeight + SEG_GAP
    }
    return { segments: stacked, top: stacked.length > 0 ? y - SEG_GAP : 0 }
  })

  const active = at !== null && at < n ? at : null
  const current = active === null ? null : series[active]

  const ringX = useMotionValue(0)
  const ringY = useMotionValue(0)
  const ringH = useMotionValue(0)
  const tipX = useMotionValue(0)
  const shown = useRef(false)
  const columnTop = active !== null && grown ? (layout[active]?.top ?? 0) : 0
  const columnLeft = active === null ? 0 : active * (barWidth + gap)
  useEffect(() => {
    if (active === null) {
      shown.current = false
      return
    }
    const right = columnLeft + barWidth + TIP_OFFSET
    const fitsRight = right + TIP_W <= width
    const fitsLeft = columnLeft - TIP_OFFSET - TIP_W >= 0
    const tip =
      fitsRight || !fitsLeft
        ? Math.max(0, Math.min(width - TIP_W, right))
        : columnLeft - TIP_OFFSET - TIP_W
    const jump = !shown.current || reduced
    glide(ringX, columnLeft - 3, jump)
    glide(ringY, chartHeight - LIFT - columnTop - 3, jump)
    glide(ringH, columnTop + LIFT + 7, jump)
    glide(tipX, tip, jump)
    shown.current = true
  }, [
    active,
    columnLeft,
    columnTop,
    barWidth,
    width,
    chartHeight,
    reduced,
    ringX,
    ringY,
    ringH,
    tipX,
  ])

  const dismissable = active !== null
  useEffect(() => {
    if (!dismissable) return
    const onDown = (event: globalThis.PointerEvent) => {
      if (!plot.current?.contains(event.target as Node)) setAt(null)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [dismissable])

  const headlineTotal = headline?.value ?? 0
  const [generation, setGeneration] = useState(0)
  const lastHeadline = useRef(headlineTotal)
  useEffect(() => {
    if (lastHeadline.current === headlineTotal) return
    lastHeadline.current = headlineTotal
    setGeneration((g) => g + 1)
  }, [headlineTotal])

  const pick = (event: PointerEvent<HTMLDivElement>) => {
    const box = plot.current?.getBoundingClientRect()
    if (!box || box.width === 0 || n === 0) return
    const index = Math.floor(((event.clientX - box.left) / box.width) * n)
    setAt(Math.max(0, Math.min(n - 1, index)))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (n === 0) return
    const from = active ?? n - 1
    const next: Record<string, number> = {
      ArrowLeft: Math.max(0, from - 1),
      ArrowDown: Math.max(0, from - 1),
      ArrowRight: Math.min(n - 1, from + 1),
      ArrowUp: Math.min(n - 1, from + 1),
      Home: 0,
      End: n - 1,
    }
    if (event.key === 'Escape') {
      setAt(null)
      return
    }
    const target = next[event.key]
    if (target === undefined) return
    event.preventDefault()
    setAt(target)
  }

  const columnText = (index: number) => {
    const point = series[index]
    if (!point) return ''
    const breakdown = visible
      .map((segment) => `${segment.label} ${fmt(point.values[segment.key] ?? 0)}`)
      .join(', ')
    return m.chartColumnText({
      date: labels[index]?.long ?? '',
      total: fmt(totals[index] ?? 0),
      breakdown,
    })
  }

  const peakIndex = totals.indexOf(Math.max(...totals))
  const summary =
    n > 0 && !empty
      ? m.chartSummary({
          first: labels[0]?.long ?? '',
          last: labels[n - 1]?.long ?? '',
          total: fmt(totals.reduce((sum, value) => sum + value, 0)),
          peak: fmt(totals[peakIndex] ?? 0),
          peakDate: labels[peakIndex]?.long ?? '',
        })
      : (emptyText ?? m.noAnalyticsDataAvailable())

  const toggleSegment = (key: K) => {
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else if (segments.length - next.size > 1) next.add(key)
      return next
    })
  }

  const periodIndex = periods
    ? Math.max(
        0,
        periods.findIndex((p) => p.value === period),
      )
    : 0
  const headlineText = headline ? fmt(headline.value) : ''
  const change = headline?.change
  const changeText =
    typeof change === 'number' && Number.isFinite(change)
      ? new Intl.NumberFormat(locale, {
          style: 'percent',
          signDisplay: 'exceptZero',
          maximumFractionDigits: 0,
        }).format(change)
      : null

  return (
    <div
      ref={root}
      className={cn('stb', className)}
      data-scrub={current ? '' : undefined}
      style={
        {
          '--stb-r': `${radius}px`,
          '--stb-rr': `${radius + 3}px`,
          '--stb-step': `${stepMs}ms`,
        } as CSSProperties
      }
    >
      {(title || periods) && (
        <div className="stb-head">
          <div className="stb-titles">
            {title && (
              <div className="stb-title">
                {icon}
                <span>{title}</span>
              </div>
            )}
            {subtitle && <div className="stb-sub">{subtitle}</div>}
          </div>
          {periods && periods.length > 1 && (
            <fieldset
              className="stb-seg-ctl"
              style={
                {
                  '--stb-count': periods.length,
                  '--stb-at': periodIndex,
                } as CSSProperties
              }
            >
              <legend className="sr-only">{m.chartPeriodLabel()}</legend>
              <span className="stb-thumb" aria-hidden />
              {periods.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={option.value === period}
                  className="stb-tab"
                  onClick={() => onPeriodChange?.(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </fieldset>
          )}
        </div>
      )}

      {headline && (
        <div className="stb-figure">
          <span className="sr-only">{headlineText}</span>
          <span className="stb-num font-serif" aria-hidden>
            {headlineText.split('').map((char, index) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: each character is keyed by its generation so a new figure replays the entrance.
                key={`${generation}-${index}`}
                className="stb-digit"
                data-pop={(generation > 0 && !reduced) || undefined}
                style={{ '--i': index } as CSSProperties}
                aria-hidden
              >
                {char}
              </span>
            ))}
          </span>
          <span className="stb-unit">{headline.label}</span>
          {changeText && (
            <span className="stb-delta" data-up={(change ?? 0) > 0 || undefined}>
              <b>{changeText}</b> {headline.changeLabel}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="stb-skeleton animate-pulse" style={{ height }} aria-hidden>
          {SKELETON_HEIGHTS.map((h, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder bars.
            <span key={index} style={{ height: `${h}%` }} />
          ))}
        </div>
      ) : empty ? (
        <div className="stb-empty" style={{ height }}>
          {emptyText ?? m.noAnalyticsDataAvailable()}
        </div>
      ) : (
        <>
          <div
            ref={plot}
            className="stb-plot"
            style={{ height }}
            role="slider"
            tabIndex={0}
            aria-label={title ?? headline?.label}
            aria-describedby={`${summaryId} ${hintId}`}
            aria-valuemin={0}
            aria-valuemax={Math.max(0, n - 1)}
            aria-valuenow={active ?? n - 1}
            aria-valuetext={active === null ? summary : columnText(active)}
            onKeyDown={onKeyDown}
            onFocus={() => setAt((prev) => prev ?? n - 1)}
            onBlur={() => setAt(null)}
            onPointerDown={pick}
            onPointerMove={(event) => {
              if (event.pointerType === 'mouse' || event.buttons > 0) pick(event)
            }}
            onPointerCancel={() => setAt(null)}
            onPointerLeave={(event) => {
              if (event.pointerType === 'mouse') setAt(null)
            }}
          >
            <span className="stb-base" style={{ top: chartHeight }} />
            {width > 0 &&
              layout.map((column, index) => (
                <div
                  key={`${dataKey}-${series[index]?.date ?? index}`}
                  className="stb-col"
                  data-on={active === index || undefined}
                  style={
                    {
                      left: index * (barWidth + gap),
                      width: barWidth,
                      '--i': index,
                    } as CSSProperties
                  }
                >
                  {column.segments.map((segment) => (
                    <span
                      key={segment.key}
                      className="stb-bar"
                      style={
                        {
                          '--c': segment.color,
                          bottom: 1 + LIFT + (grown ? segment.bottom : 0),
                          height: grown ? segment.height : 0,
                        } as CSSProperties
                      }
                    />
                  ))}
                </div>
              ))}

            <motion.span
              className="stb-ring"
              aria-hidden
              style={{ x: ringX, y: ringY, height: ringH, width: barWidth + 6 }}
            />

            <motion.div
              className="stb-tip"
              style={{ x: tipX, width: Math.min(TIP_W, width) }}
              aria-hidden
            >
              {current && active !== null && (
                <>
                  <div className="stb-tip-head">
                    <span>{labels[active]?.long}</span>
                    <b>{fmt(totals[active] ?? 0)}</b>
                  </div>
                  {[...visible].reverse().map((segment) => (
                    <div className="stb-tip-row" key={segment.key}>
                      <i className="stb-swatch" style={{ '--c': segment.color } as CSSProperties} />
                      <span>{segment.label}</span>
                      <b>{fmt(current.values[segment.key] ?? 0)}</b>
                    </div>
                  ))}
                </>
              )}
            </motion.div>
          </div>
          <p id={summaryId} className="sr-only">
            {summary}
          </p>
          <p id={hintId} className="sr-only">
            {m.chartKeyboardHint()}
          </p>

          <div className="stb-axis" aria-hidden>
            <span>{labels[0]?.short}</span>
            {n > 1 && <span>{labels[n - 1]?.short}</span>}
          </div>
        </>
      )}

      {legend && segments.length > 1 && !loading && !empty && (
        <fieldset className="stb-legend m-0 border-0 p-0">
          <legend className="sr-only">{m.chartLegendLabel()}</legend>
          {segments.map((segment) => (
            <button
              key={segment.key}
              type="button"
              className="stb-chip"
              aria-pressed={!hidden.has(segment.key)}
              onClick={() => toggleSegment(segment.key)}
            >
              <i className="stb-swatch" style={{ '--c': segment.color } as CSSProperties} />
              {segment.label}
            </button>
          ))}
        </fieldset>
      )}
    </div>
  )
}
